/**
 * @file AddCompanyCardDialog.tsx
 * @brief Stripe SetupIntent flow for adding a company card (FormSheet).
 *
 * @author DES
 * @date 19-06-2026
 * @updated 29-07-2026 — FormSheet + GridBlock chrome
 */

import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import type { StripePaymentElementOptions } from "@stripe/stripe-js";
import { LoaderCircle } from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/Button";
import {
  FormSheet,
  FormSheetBody,
  FormSheetContent,
  FormSheetDescription,
  FormSheetField,
  FormSheetFields,
  FormSheetFooter,
  FormSheetFooterCell,
  FormSheetFrame,
  FormSheetHeader,
  FormSheetSection,
  FormSheetTitle,
  formSheetActionClass,
} from "@/components/ui/FormSheet";
import { ApiClientError } from "@/shared/api/client";
import { findCountry } from "@/shared/data/countries";
import { toast } from "@/shared/hooks/useToast";
import { cn } from "@/shared/theme/cn";
import { getStripe } from "@/shared/stripe/stripeClient";
import {
  useCompanyBillingProfile,
  useCreateCompanySetupIntent,
  useSetCompanyDefaultPaymentMethod,
  useUpdateCompanyBillingProfile,
  companyBillingKeys,
} from "../api";
import {
  isBillingAddressComplete,
  type CompanyBillingProfile,
} from "../schemas";
import {
  CompanyBillingAddressFields,
  type BillingAddressFormValues,
} from "./CompanyBillingAddressFields";

/** Card-only Stripe form; address comes from company profile below. */
const PAYMENT_ELEMENT_OPTIONS: StripePaymentElementOptions = {
  layout: "tabs",
  wallets: {
    applePay: "never",
    googlePay: "never",
    link: "never",
  },
  fields: {
    // Email / phone were "never" but never passed to confirmSetup, which
    // Stripe refuses (IntegrationError). Cards don't ask for them (17-10-2026).
    billingDetails: {
      name: "never",
      address: "auto",
    },
  },
};

function profileToAddressForm(
  profile: CompanyBillingProfile | undefined,
): BillingAddressFormValues {
  return {
    address: profile?.address?.trim() ?? "",
    city: profile?.city?.trim() ?? "",
    state: profile?.state?.trim() ?? "",
    postal_code: profile?.postal_code?.trim() ?? "",
    country: profile?.country?.trim() ?? "",
  };
}

function stripeCountryCode(countryName: string): string | undefined {
  const match = findCountry(countryName);
  return match?.code;
}

interface AddCompanyCardDialogProps {
  companyId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  setAsDefault?: boolean;
}

function AddCardPayment({
  companyId,
  companyName,
  addressForm,
  setAsDefault,
  onSuccess,
  onSaveAddress,
  setSaving,
  submitRef,
}: {
  companyId: string;
  companyName: string;
  addressForm: BillingAddressFormValues;
  setAsDefault: boolean;
  onSuccess: () => void;
  onSaveAddress: () => Promise<void>;
  setSaving: (v: boolean) => void;
  submitRef: MutableRefObject<(() => Promise<void>) | null>;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const setDefault = useSetCompanyDefaultPaymentMethod();
  const qc = useQueryClient();

  useEffect(() => {
    submitRef.current = async () => {
      if (!stripe || !elements) return;

      if (!addressForm.address.trim() || !addressForm.country.trim()) {
        toast({
          variant: "destructive",
          title: "Billing address required",
          description:
            "Enter street address and country before saving the card.",
        });
        return;
      }

      const countryCode = stripeCountryCode(addressForm.country);
      if (!countryCode) {
        toast({
          variant: "destructive",
          title: "Invalid country",
          description: "Pick a country from the list.",
        });
        return;
      }

      setSaving(true);
      try {
        await onSaveAddress();

        const { error, setupIntent } = await stripe.confirmSetup({
          elements,
          confirmParams: {
            return_url: window.location.href,
            payment_method_data: {
              billing_details: {
                name: companyName,
                address: {
                  line1: addressForm.address.trim(),
                  city: addressForm.city.trim() || undefined,
                  state: addressForm.state.trim() || undefined,
                  postal_code: addressForm.postal_code.trim() || undefined,
                  country: countryCode,
                },
              },
            },
          },
          redirect: "if_required",
        });

        if (error) {
          toast({
            variant: "destructive",
            title: "Card not saved",
            description: error.message ?? "Please check your card details.",
          });
          return;
        }

        const pmId =
          typeof setupIntent?.payment_method === "string"
            ? setupIntent.payment_method
            : setupIntent?.payment_method?.id;

        if (setAsDefault && pmId) {
          await setDefault.mutateAsync({
            companyId,
            paymentMethodId: pmId,
          });
        } else {
          await qc.invalidateQueries({
            queryKey: companyBillingKeys.paymentMethods(companyId),
          });
        }

        toast({ variant: "success", title: "Card saved" });
        onSuccess();
      } catch (err) {
        toast({
          variant: "destructive",
          title: "Card not saved",
          description:
            err instanceof ApiClientError ? err.message : "Please try again.",
        });
      } finally {
        setSaving(false);
      }
    };

    return () => {
      submitRef.current = null;
    };
  }, [
    addressForm,
    companyId,
    companyName,
    elements,
    onSaveAddress,
    onSuccess,
    qc,
    setAsDefault,
    setDefault,
    setSaving,
    stripe,
    submitRef,
  ]);

  return <PaymentElement options={PAYMENT_ELEMENT_OPTIONS} />;
}

export function AddCompanyCardDialog({
  companyId,
  open,
  onOpenChange,
  setAsDefault = true,
}: AddCompanyCardDialogProps) {
  const profileQuery = useCompanyBillingProfile();
  const updateProfile = useUpdateCompanyBillingProfile();
  const setupIntent = useCreateCompanySetupIntent();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [addressForm, setAddressForm] = useState<BillingAddressFormValues>(
    profileToAddressForm(undefined),
  );
  const submitRef = useRef<(() => Promise<void>) | null>(null);

  const companyName = profileQuery.data?.name ?? "Company";

  useEffect(() => {
    if (profileQuery.data) {
      setAddressForm(profileToAddressForm(profileQuery.data));
    }
  }, [profileQuery.data]);

  useEffect(() => {
    if (!open) {
      setClientSecret(null);
      setSaving(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await setupIntent.mutateAsync(companyId);
        if (!cancelled) setClientSecret(res.clientSecret);
      } catch (err) {
        if (!cancelled) {
          toast({
            variant: "destructive",
            title: "Couldn't start card setup",
            description:
              err instanceof ApiClientError
                ? err.message
                : "Please try again.",
          });
          onOpenChange(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fresh intent each open
  }, [open, companyId]);

  const addressHint = useMemo(() => {
    if (profileQuery.isLoading) return null;
    if (isBillingAddressComplete(profileQuery.data)) {
      return "Using your company billing address. Update below if it changed.";
    }
    return "Add your company billing address. Some cards and banks require it.";
  }, [profileQuery.data, profileQuery.isLoading]);

  const persistAddress = useCallback(async () => {
    await updateProfile.mutateAsync({
      address: addressForm.address.trim(),
      city: addressForm.city.trim() || undefined,
      state: addressForm.state.trim() || undefined,
      postal_code: addressForm.postal_code.trim() || undefined,
      country: addressForm.country.trim(),
    });
  }, [addressForm, updateProfile]);

  const handleClose = useCallback(() => onOpenChange(false), [onOpenChange]);

  const loading =
    profileQuery.isLoading || setupIntent.isPending || !clientSecret;

  return (
    <FormSheet open={open} onOpenChange={onOpenChange}>
      {/* Cmd+K-style palette frame (DES 01-10-2026: form standard). */}
      <FormSheetContent side="palette">
        <FormSheetFrame>
          <FormSheetHeader>
            <FormSheetTitle>Add company card</FormSheetTitle>
            <FormSheetDescription>
              Saved for corporate session billing. Billing address is stored on
              your company profile.
            </FormSheetDescription>
          </FormSheetHeader>

          <FormSheetBody>
            {/* Address and card side by side on wide screens so the sheet is
                short (DES 01-10-2026: form standard). Layout only: the
                Stripe element and its options are unchanged. */}
            <div className="grid md:grid-cols-2 md:divide-x md:divide-border/80 [&>*]:min-w-0">
            <FormSheetSection
              title="Billing address"
              subtitle={addressHint ?? undefined}
            >
              <FormSheetFields>
                <FormSheetField span={2}>
                  <CompanyBillingAddressFields
                    values={addressForm}
                    onChange={setAddressForm}
                    disabled={loading || updateProfile.isPending || saving}
                    flush
                  />
                </FormSheetField>
              </FormSheetFields>
            </FormSheetSection>

            <FormSheetSection
              title="Card"
              subtitle="Card details are handled by Stripe. We never store the number."
            >
              <FormSheetFields>
                <FormSheetField span={2}>
                  {loading ? (
                    <div className="flex justify-center py-10">
                      <LoaderCircle className="h-8 w-8 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    <Elements
                      stripe={getStripe()}
                      options={{ clientSecret: clientSecret! }}
                    >
                      <AddCardPayment
                        companyId={companyId}
                        companyName={companyName}
                        addressForm={addressForm}
                        setAsDefault={setAsDefault}
                        onSuccess={handleClose}
                        onSaveAddress={persistAddress}
                        setSaving={setSaving}
                        submitRef={submitRef}
                      />
                    </Elements>
                  )}
                </FormSheetField>
              </FormSheetFields>
            </FormSheetSection>
            </div>
          </FormSheetBody>

          <FormSheetFooter>
            <FormSheetFooterCell>
              <Button
                type="button"
                variant="ghost"
                onClick={handleClose}
                disabled={saving}
                className={formSheetActionClass}
              >
                Cancel
              </Button>
            </FormSheetFooterCell>
            <FormSheetFooterCell>
              <Button
                type="button"
                disabled={saving || loading}
                onClick={() => void submitRef.current?.()}
                className={cn(
                  formSheetActionClass,
                  "bg-primary text-primary-foreground hover:bg-primary/90",
                )}
              >
                {saving ? (
                  <>
                    <LoaderCircle className="h-4 w-4 animate-spin" />
                    Saving…
                  </>
                ) : (
                  "Save card"
                )}
              </Button>
            </FormSheetFooterCell>
          </FormSheetFooter>
        </FormSheetFrame>
      </FormSheetContent>
    </FormSheet>
  );
}
